import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';

import { useQcpIssueTicket } from '@/hooks/useQcpIssueTicket';
import {
  ClientEvents,
  ServerEvents,
  type MmEnqueue,
  type MmMatchedPayload,
} from '@/lib/qcp/protocol';
import { openQcpSocket } from '@/lib/qcp/socket';

/**
 * The two configured DEFAULT_BUCKETS on the QCP gameserver.
 *
 * CRITICAL: The server only pairs players within the SAME bucket. Sending any
 * other (bank, inc) pair enqueues the player but they will NEVER be matched.
 * This type is a discriminated union of the only two valid payloads the UI
 * may emit — a free-form clock picker is intentionally absent.
 */
export type CpQueueBucket =
  | { bank: 60; inc: 5 }    // "Fast"     — 1:00 bank + 0:05 increment
  | { bank: 120; inc: 10 }; // "Standard" — 2:00 bank + 0:10 increment

/** The two hardcoded preset values (one per allowed server bucket). */
export const CP_QUEUE_BUCKETS: [CpQueueBucket, CpQueueBucket] = [
  { bank: 60, inc: 5 },
  { bank: 120, inc: 10 },
];

export type CpQueueStatus = 'idle' | 'searching' | 'matched';

export interface CpQueueController {
  status: CpQueueStatus;
  /** ISO timestamp at which we emitted `mm:enqueue`, used for the elapsed clock. */
  enqueuedAt: number | null;
  /** Set once the server pairs us (`mm:matched`) — the caller navigates to the match. */
  matched: string | null;
  error: string | null;
  /**
   * Emit `mm:enqueue` with a configured bucket.
   * The bucket MUST be one of `CP_QUEUE_BUCKETS` — the type system enforces this.
   */
  enqueue: (bucket: MmEnqueue) => void;
  /** Disconnect the socket and reset to idle (Cancel button / backdrop tap). */
  leave: () => void;
}

/**
 * Drives the QCP ranked matchmaking queue from the TMA.
 *
 * Lifecycle: opens ONE Socket.IO connection to the QCP gameserver (a Limulo
 * TICKET with purpose `queue`, as `useCpInvite` / `useQcpMatch`) on mount, then keeps it open while waiting
 * for a match.  The socket is purely a listener until `enqueue()` is called;
 * it does NOT join any match room.
 *
 * `enqueue(bucket)` emits `mm:enqueue` to the server with the chosen clock
 * preset. The server places the player in the queue for that bucket and, once
 * paired, emits `mm:matched { matchId }` back over the socket.  On receipt the
 * hook sets `status = 'matched'` and exposes the paired `matchId` via
 * `matched` — the caller (CpLobby) reacts to it and calls `startMatch` so the
 * new match is registered as the one active match before navigating.
 *
 * `leave()` disconnects the socket (the server self-dequeues on disconnect)
 * and resets state to `idle`.  On unmount the socket is always disconnected to
 * prevent leaks — the close also stops a socket whose ticket has not arrived yet.
 * If the socket cannot be opened (`error` set, status back to `idle`), the next
 * `enqueue()` asks a NEW ticket and opens a new socket: that is the «Riprova».
 *
 * Clock presets are constrained to `CpQueueBucket` — only the two values that
 * match the server's `DEFAULT_BUCKETS` ({bank:60,inc:5} and {bank:120,inc:10})
 * are accepted by the type system. A non-bucket pair would enqueue but never
 * pair (the user waits forever), so the UI must never expose a free-form picker.
 */
export function useCpQueue(): CpQueueController {
  // The open socket (set by `onSocket`) and the close of the whole `openQcpSocket` (null = none open).
  const socketRef = useRef<Socket | null>(null);
  const closeRef = useRef<(() => void) | null>(null);
  const issueTicket = useQcpIssueTicket();
  // Desired bucket to (re)emit once the socket is connected. Set by enqueue();
  // flushed in the socket 'connect' handler so a tap made BEFORE the async
  // socket creation (the async ticket request) isn't silently lost.
  const pendingRef = useRef<MmEnqueue | null>(null);
  const [status, setStatus] = useState<CpQueueStatus>('idle');
  const [enqueuedAt, setEnqueuedAt] = useState<number | null>(null);
  const [matched, setMatched] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Opens the listen socket with a NEW ticket (purpose `queue`). `mm:matched` arrives here after the
  // server pairs us; no `match:join` emit needed (we're not joining a room yet, just registering for
  // matchmaking events). `onSocket` runs for every socket opened, reconnections included.
  const open = useCallback(() => {
    if (closeRef.current) return;
    closeRef.current = openQcpSocket({
      purpose: 'queue',
      issueTicket,
      // No socket (ticket refused, capability off, address not allowed, handshake refused, dropped too often).
      onFail: () => {
        closeRef.current = null;
        socketRef.current = null;
        setError('connection failed');
        setStatus('idle');
      },
      onSocket: (socket) => {
        socketRef.current = socket;

        // Flush a pending enqueue once connected — covers the race where the user
        // taps a bucket BEFORE this socket finished its async ticket/creation.
        socket.on('connect', () => {
          if (pendingRef.current) socket.emit(ClientEvents.MmEnqueue, pendingRef.current);
        });

        // Server-side matchmaking error (bad payload / unauthorized / mm_failed).
        socket.on(ServerEvents.Err, (p: { message?: string }) => {
          setError(p?.message ?? 'queue error');
          setStatus('idle');
        });

        socket.on(ServerEvents.MmMatched, (p: MmMatchedPayload) => {
          pendingRef.current = null;
          setStatus('matched');
          setMatched(p.matchId);
        });
      },
    });
  }, [issueTicket]);

  // Per-effect run, NOT a shared ref flag (StrictMode mounts → unmounts → remounts: a flag set by the
  // first cleanup would stay set through the remount and the socket would never be created).
  useEffect(() => {
    open();
    return () => {
      // Close on unmount — the gameserver self-dequeues when the socket drops.
      closeRef.current?.();
      closeRef.current = null;
      socketRef.current = null;
    };
  }, [open]);

  const enqueue = useCallback((bucket: MmEnqueue) => {
    setError(null);
    setEnqueuedAt(Date.now());
    setMatched(null);
    setStatus('searching');
    // Remember the desired bucket so the 'connect' handler can (re)send it if
    // the socket isn't ready yet.
    pendingRef.current = bucket;
    // After a failed open (or a `leave()`) there is no socket: ask a NEW ticket and open one.
    open();
    // If already connected, emit now; otherwise the 'connect' handler flushes
    // pendingRef once the socket comes up.
    if (socketRef.current?.connected) {
      socketRef.current.emit(ClientEvents.MmEnqueue, bucket);
    }
  }, [open]);

  const leave = useCallback(() => {
    setStatus('idle');
    setEnqueuedAt(null);
    setMatched(null);
    setError(null);
    pendingRef.current = null;
    closeRef.current?.();
    closeRef.current = null;
    socketRef.current = null;
  }, []);

  return { status, enqueuedAt, matched, error, enqueue, leave };
}
